import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import { blogPosts } from '../../mocks/blogs';
import HeroBackground from '../../components/feature/HeroBackground';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const buildBlogSchema = () => [
  {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${SITE_URL}/blog`,
    url: `${SITE_URL}/blog`,
    name: 'Insights & Resources – True Passage Ventures',
    description: 'Expert guidance on sourcing, manufacturing, and global trade from India. Stay informed with the latest articles and industry insights from True Passage Ventures.',
    publisher: {
      '@type': 'Organization',
      name: 'True Passage Ventures',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/vite.svg`,
      },
    },
    blogPost: blogPosts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      url: `${SITE_URL}/blog/${post.slug}`,
      datePublished: post.date,
      description: post.excerpt,
      image: post.heroImage,
      author: {
        '@type': 'Organization',
        name: 'True Passage Ventures',
      },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
    ],
  },
];

const BlogPage = () => {
  const blogSchema = buildBlogSchema();

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Blog – India Sourcing & Supply Chain Insights | True Passage Ventures"
        description="Expert articles on India sourcing, OEM manufacturing, export logistics, and global supply chain management. Stay ahead with True Passage Ventures industry insights."
        keywords="India sourcing blog, supply chain articles, manufacturing insights, export logistics tips, global trade resources"
        canonical="/blog"
        schema={blogSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-primary via-navy-primary to-navy-primary/90 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Insights &amp; Resources
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Expert guidance on sourcing, manufacturing, and global trade from India. Stay informed with our latest articles and industry insights.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    <div className="relative w-full h-56 overflow-hidden">
                      <img
                        src={post.heroImage}
                        alt={post.title}
                        title={`${post.title} – True Passage Ventures`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="inline-block px-3 py-1 bg-gold-accent text-white text-xs font-medium rounded-full whitespace-nowrap">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 text-sm text-navy-primary/60 mb-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {new Date(post.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {post.readTime}
                        </span>
                      </div>

                      <h2 className="text-xl font-bold text-navy-primary mb-3 group-hover:text-gold-accent transition-colors line-clamp-2">
                        {post.title}
                      </h2>

                      <p className="text-navy-primary/70 mb-4 line-clamp-3 flex-1">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center text-gold-accent font-medium group-hover:gap-2 transition-all whitespace-nowrap">
                        Read Article
                        <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-primary to-navy-primary/90">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Start Sourcing?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Let our team help you navigate the complexities of global sourcing and connect you with verified Indian suppliers.
            </p>
            <Link
              to="/request-quote"
              className="inline-block px-8 py-4 bg-gold-accent text-white font-medium rounded-lg hover:bg-gold-accent/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Request a Quote
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
